// frontend/src/hooks/useAuth.ts
// ✅ FIXED: Added all missing methods and proper error handling

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  authService,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  PasswordResetData,
  PasswordResetConfirmData,
  ChangePasswordData
} from '@/lib/api/authService';
import { tokenManager } from '@/lib/httpClient';
import { useAuth as useAuthContext } from '@/contexts/AuthContext';

// Query keys for auth - following the pattern from other services
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
  verification: () => [...authKeys.all, 'verification'] as const,
  sessions: () => [...authKeys.all, 'sessions'] as const,
} as const;

// Get current user profile
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['auth', 'current-user'],
    queryFn: async () => {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        // ✅ FIXED: Return the user data from the nested structure
        return response.data.user;
      }
      throw new Error(response.message || 'Failed to fetch current user');
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Login user
export const useLogin = () => {
  const queryClient = useQueryClient();
  const { setUserData, setEmailVerificationData } = useAuthContext();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<AuthResponse> => {
      const response = await authService.login(credentials);
      if (!response.success || !response.data) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (authData: AuthResponse) => {
      // Store tokens
      console.log('Login successful, storing tokens:', {
        hasAccessToken: !!authData.token,
        hasRefreshToken: !!authData.refresh_token
      });
      tokenManager.setTokens(authData.token, authData.refresh_token);
      
      // Update context state
      setUserData(authData.user);
      setEmailVerificationData(authData.email_verification || null);
      
      // Cache user data
      queryClient.setQueryData(authKeys.user(), authData.user);
      
      // Invalidate auth-related queries to refetch with new permissions
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
};

// Register customer
export const useRegisterCustomer = () => {
  const queryClient = useQueryClient();
  const { setUserData, setEmailVerificationData } = useAuthContext();

  return useMutation({
    mutationFn: async (data: RegisterData): Promise<AuthResponse> => {
      const response = await authService.registerCustomer(data);
      if (!response.success || !response.data) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (authData: AuthResponse) => {
      // Store tokens
      tokenManager.setTokens(authData.token, authData.refresh_token);
      
      // Update context state
      setUserData(authData.user);
      setEmailVerificationData(authData.email_verification || null);
      
      // Cache user data
      queryClient.setQueryData(authKeys.user(), authData.user);
      
      // Invalidate auth queries
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
};

// Register partner/provider
export const useRegisterPartner = () => {
  const queryClient = useQueryClient();
  const { setUserData, setEmailVerificationData } = useAuthContext();

  return useMutation({
    mutationFn: async (data: RegisterData): Promise<AuthResponse> => {
      const response = await authService.registerPartner(data);
      if (!response.success || !response.data) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (authData: AuthResponse) => {
      // Store tokens
      tokenManager.setTokens(authData.token, authData.refresh_token);
      
      // Update context state
      setUserData(authData.user);
      setEmailVerificationData(authData.email_verification || null);
      
      // Cache user data
      queryClient.setQueryData(authKeys.user(), authData.user);
      
      // Invalidate auth queries
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
};

// Logout user
export const useLogout = () => {
  const queryClient = useQueryClient();
  const { clearAuth } = useAuthContext();

  return useMutation({
    mutationFn: async () => {
      const response = await authService.logout();
      return response;
    },
    onSuccess: () => {
      // Clear context state
      clearAuth();
      
      // Clear all cached data
      queryClient.clear();
    },
    onError: () => {
      // Even if logout fails on server, clear local data
      clearAuth();
      queryClient.clear();
    },
  });
};

// ✅ ADDED: Request password reset
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: async (data: PasswordResetData) => {
      const response = await authService.requestPasswordReset(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// ✅ ADDED: Confirm password reset
export const useConfirmPasswordReset = () => {
  return useMutation({
    mutationFn: async (data: PasswordResetConfirmData) => {
      const response = await authService.confirmPasswordReset(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// ✅ ADDED: Change password
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: ChangePasswordData) => {
      const response = await authService.changePassword(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// ✅ ADDED: Update profile
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { setUserData } = useAuthContext();

  return useMutation({
    mutationFn: async (profileData: any) => {
      const response = await authService.updateProfile(profileData);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Update context and cache
      if (data.user) {
        setUserData(data.user);
        queryClient.setQueryData(authKeys.user(), data.user);
      }
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    },
  });
};

// ✅ ADDED: Upload profile image
export const useUploadProfileImage = () => {
  const queryClient = useQueryClient();
  const { setUserData } = useAuthContext();

  return useMutation({
    mutationFn: async (file: File) => {
      const response = await authService.uploadProfileImage(file);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Update context and cache
      if (data.user) {
        setUserData(data.user);
        queryClient.setQueryData(authKeys.user(), data.user);
      }
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    },
  });
};

// ✅ ADDED: Resend verification email
export const useResendVerificationEmail = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await authService.resendVerificationEmail();
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// ✅ ADDED: Verify email
export const useVerifyEmail = () => {
  const queryClient = useQueryClient();
  const { setUserData } = useAuthContext();

  return useMutation({
    mutationFn: async (token: string) => {
      const response = await authService.verifyEmail(token);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Update context and cache
      if (data.user) {
        setUserData(data.user);
        queryClient.setQueryData(authKeys.user(), data.user);
      }
      queryClient.invalidateQueries({ queryKey: authKeys.verification() });
    },
  });
};

// ✅ ADDED: Get active sessions
export const useActiveSessions = () => {
  return useQuery({
    queryKey: authKeys.sessions(),
    queryFn: async () => {
      const response = await authService.getActiveSessions();
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// ✅ ADDED: Revoke session
export const useRevokeSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await authService.revokeSession(sessionId);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.sessions() });
    },
  });
};

// ✅ ADDED: Revoke all sessions
export const useRevokeAllSessions = () => {
  const queryClient = useQueryClient();
  const { clearAuth } = useAuthContext();

  return useMutation({
    mutationFn: async () => {
      const response = await authService.revokeAllSessions();
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      // Clear auth state since all sessions are revoked
      clearAuth();
      queryClient.clear();
    },
  });
};

// ✅ ADDED: Delete account
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  const { clearAuth } = useAuthContext();

  return useMutation({
    mutationFn: async (password: string) => {
      const response = await authService.deleteAccount(password);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      // Clear auth state since account is deleted
      clearAuth();
      queryClient.clear();
    },
  });
};
